import styled, {keyframes} from "styled-components";
import {motion} from "theme/tokens";
import {BODY_PADDING_TOP, BODY_PADDING_TOP_MOBILE} from "components/CardSheet/CardSheet.styles";

/**
 * The personalization sheet's own chrome — the grammar every stacked section shares, owned by
 * {@link ./BrandSheet} because the SHEET decides how its sections are separated, not any one of them.
 *
 * These began inside `PageThemeSection.styles.ts`, which was correct while the theme was the only
 * section below the brand editor. ui#529 added a third ("Your address") and gave the brand editor a
 * heading of its own, so keeping them there would have made one section the landlord of the other two's
 * separator — and a second copy is how one sheet starts reading as two.
 */

/**
 * Sections rise into place as the sheet slides in, staggered so the eye lands on the first one.
 *
 * Small on purpose: 8px and a fade. The card itself is already travelling its full width
 * ({@link ../../components/CardSheet/CardSheet.styles}), so anything larger here reads as two competing
 * animations rather than one surface settling.
 */
const settle = keyframes`
	from {
		opacity: 0;
		transform: translateY(8px);
	}
	to {
		opacity: 1;
		transform: none;
	}
`;

/** Fade only — the reduced-motion counterpart of {@link settle}, with no travel at all. */
const settleReduced = keyframes`
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
`;

/**
 * One stacked editor: "Your brand", "Download page theme" (api#511), "Your address" (ui#529).
 *
 * The FIRST one draws no rule and adds no top gap — it sits directly under the sheet header, which is
 * already a separator. Letting `:first-child` decide means the sections stay reorderable and a section
 * that is gated away (the theme editor is absent entirely for a non-entitled sender) cannot leave a
 * stray hairline behind, which a fixed "the brand one is first" rule would.
 */
export const Section = styled.section`
	display: flex;
	flex-direction: column;
	gap: 12px;
	margin-top: 22px;
	padding-top: 20px;
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	animation: ${settle} ${motion.enter} ${motion.easeOut} both;

	&:first-child {
		margin-top: 0;
		padding-top: 0;
		border-top: none;
	}

	/* The stagger is deliberately short and capped: by the third section the sheet has finished
	   travelling, so a longer ladder would animate into an already-still surface. */
	&:nth-child(2) {
		animation-delay: 60ms;
	}

	&:nth-child(n + 3) {
		animation-delay: 120ms;
	}

	@media (prefers-reduced-motion: reduce) {
		animation: ${settleReduced} 120ms linear both;
		animation-delay: 0ms;
	}
`;

/**
 * A stacked section's heading. An `h3` under the sheet's own `h2` title, so the sheet keeps one heading
 * tree — "Personalization" > {Your brand, Download page theme, Your address} — rather than each section
 * inventing a level or borrowing the sheet's.
 */
export const SectionTitle = styled.h3`
	margin: 0;
	font-size: 15px;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
`;

/**
 * The tab strip's own row (ui#648), sticky so the three areas stay switchable while a long editor —
 * the theme section's image list, say — scrolls underneath.
 *
 * On a 375px phone the three segments are wider than the sheet; `SegmentedTabs` scrolls its own rail
 * sideways then (kitecorp/transferify-ui#3013), so this row only pins and paints the strip.
 *
 * `flex: none` keeps the row at its content height: the card sheet's scroll region is a COLUMN FLEX
 * container, and a shrinking row here once squashed to an 8px `card`-filled band sticking over the
 * content (the "white line" the sheet shipped with).
 */
export const TabBar = styled.div`
	flex: none;
	position: sticky;
	/*
	 * Pins to the top of the VISIBLE scroll area, which is not where a zero offset puts it: a sticky
	 * offset resolves against the body's content box, so zero parks the bar a full BODY_PADDING_TOP
	 * below the header and leaves that much of a window for the section headings to slide through above
	 * it. Cancelling the padding closes the window — see the constant for why it is shared rather than
	 * retyped here.
	 */
	top: calc(-1 * ${BODY_PADDING_TOP});
	z-index: 1;
	/* See {@link TabPanel} for why every part of this sheet states its own alignment. */
	text-align: left;
	margin: -4px -4px 18px;
	padding: 4px;
	background: ${({theme}) => theme.tokens.card};

	@media (max-width: 640px) {
		top: calc(-1 * ${BODY_PADDING_TOP_MOBILE});
	}
`;

/**
 * One tab's contents.
 *
 * Inactive panels stay MOUNTED and are hidden with the `hidden` attribute rather than unmounted, and
 * that is behaviour rather than styling. Each editor's hook holds its own unsaved state and fetches on
 * mount, so unmounting on every tab switch would both re-issue `GetBrand` and silently throw
 * away a colour the sender had picked but not yet saved — ui#648 is a presentation change, and losing
 * pending edits would make it anything but. `hidden` also keeps the inactive panels out of the
 * accessibility tree, so a screen reader and Playwright both see only the active one.
 */
export const TabPanel = styled.div`
	display: flex;
	flex-direction: column;
	/*
	 * The sheet's editors are LEFT aligned, and they have to say so. AppRoot (in AppComponents) sets
	 * text-align: center on the whole app shell, which every section here inherits now that the sheet is
	 * a card in the page tree rather than a modal portaled out to the document body — the same
	 * inheritance the shared card head already states its own left edge against. Without this, section
	 * headings, hints and upsell copy centre themselves the moment the sheet is opened.
	 */
	text-align: left;

	&[hidden] {
		display: none;
	}
`;
